/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * HARMONOGRAM SKÓREK (OWNER 21.10) — oś czasu zastosowań: kto, gdzie, od kiedy, do kiedy
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ┌─ WARSTWA WIDOKU, NIE DRUGIE ŹRÓDŁO PRAWDY ─────────────────────────────────────────────────────┐
 * │ Bloki powstają z tych samych list co biblioteka: zastosowania Skórek 2.0 (`/skorki/zastosowania`)│
 * │ i publikacje V1 (rewizje z oknem, zawsze „Wszyscy”). Stan bloku = `effectiveStatus` serwera —   │
 * │ tu nic nie jest rozstrzygane na nowo; kto co zobaczy, liczy resolver systemu głównego.          │
 * └────────────────────────────────────────────────────────────────────────────────────────────────┘
 *
 * Oś liczy DNI WARSZAWSKIE: dzień zaczyna się o północy w Warszawie, więc doba zmiany czasu ma 23 albo
 * 25 godzin i blok kończący się o 23:59 kończy się dokładnie przy krawędzi swojego dnia.
 */
import type { AdresatSkorki, Skorka, ZakresPaska, ZastosowanieSkorki } from '@/lib/api/tresci';
import { PALETA_RESTAURACJI, kolorTekstu } from '@/lib/grafik/analiza';
import { utcNaWarszawe, warszawaNaUtc } from './czasWarszawa';

export type StanBloku = 'ACTIVE' | 'SCHEDULED' | 'ENDED' | 'DISABLED';

export interface BlokHarmonogramu {
  /** `z-<id zastosowania>` albo `v1-<id rewizji>`. */
  klucz: string;
  zastosowanie: ZastosowanieSkorki | null;
  /** Publikacja sprzed Skórek 2.0 (V1) — zawsze dla wszystkich. */
  v1: Skorka | null;
  themeId: string;
  nazwa: string;
  miniatura: string | null;
  zakres: ZakresPaska;
  adresaci: AdresatSkorki[];
  priorytet: number;
  stan: StanBloku;
  /** Początek w ms UTC (brak daty startu = od publikacji). */
  start: number;
  /** Koniec planowany w ms UTC; `null` = bez końca. */
  koniec: number | null;
  /** Koniec rysowany: wcześniejsze wyłączenie skraca blok. */
  koniecRysowany: number | null;
  kolor: string;
  kolorTekstu: string;
}

/** Litera zakresu na bloku — kolor należy do skórki, zakres niesie etykieta. */
export const LITERA_ZAKRESU: Readonly<Record<ZakresPaska, string>> = { GLOBAL: 'W', CITY: 'M', SCHOOL: 'S', USER: 'O' };

export const TORY: readonly { zakres: ZakresPaska; etykieta: string }[] = [
  { zakres: 'GLOBAL', etykieta: 'Wszyscy' },
  { zakres: 'CITY', etykieta: 'Miasta' },
  { zakres: 'SCHOOL', etykieta: 'Szkoły' },
  { zakres: 'USER', etykieta: 'Osoby' },
];

const ms = (s: string | null | undefined): number => (s ? Date.parse(s) : Number.NaN);
const bierzacy = (b: { stan: StanBloku }) => b.stan === 'ACTIVE' || b.stan === 'SCHEDULED';

/**
 * Kolor = SKÓRKA (ta sama skórka ma ten sam kolor w każdym torze). Kolejność przydziału po najstarszej
 * rewizji skórki, więc nowa skórka w bibliotece nie przestawia kolorów już istniejących.
 */
export function koloryskorek(rewizje: readonly Skorka[], zastosowania: readonly ZastosowanieSkorki[]): Map<string, string> {
  const najstarsza = new Map<string, number>();
  const dopisz = (themeId: string, id: number) => {
    const obecna = najstarsza.get(themeId);
    if (obecna === undefined || id < obecna) najstarsza.set(themeId, id);
  };
  rewizje.forEach((r) => dopisz(r.themeId, r.id));
  zastosowania.forEach((z) => dopisz(z.themeId, z.revisionId));
  const kolejnosc = [...najstarsza].sort((a, b) => a[1] - b[1] || a[0].localeCompare(b[0]));
  return new Map(kolejnosc.map(([themeId], i) => [themeId, PALETA_RESTAURACJI[i % PALETA_RESTAURACJI.length] as string]));
}

export function zbudujBloki(zastosowania: readonly ZastosowanieSkorki[], rewizje: readonly Skorka[]): BlokHarmonogramu[] {
  const kolory = koloryskorek(rewizje, zastosowania);
  const kolor = (themeId: string) => kolory.get(themeId) ?? PALETA_RESTAURACJI[0];
  const nazwy = new Map(rewizje.map((r) => [r.themeId, r.name]));
  const miniatury = new Map<string, string>();
  rewizje.forEach((r) => { if (r.thumbnail && !miniatury.has(r.themeId)) miniatury.set(r.themeId, r.thumbnail); });

  const bloki: BlokHarmonogramu[] = [];
  for (const z of zastosowania) {
    const start = ms(z.startsAt) || ms(z.publishedAt);
    if (Number.isNaN(start)) continue;
    const koniec = z.endsAt ? ms(z.endsAt) : null;
    const wylaczono = z.effectiveStatus === 'DISABLED' && z.disabledAt ? ms(z.disabledAt) : null;
    const k = kolor(z.themeId);
    bloki.push({
      klucz: `z-${z.id}`, zastosowanie: z, v1: null, themeId: z.themeId,
      nazwa: z.name ?? nazwy.get(z.themeId) ?? 'skórka', miniatura: z.thumbnail ?? miniatury.get(z.themeId) ?? null,
      zakres: z.scope, adresaci: z.targets ?? [], priorytet: z.priority, stan: z.effectiveStatus, start, koniec,
      koniecRysowany: wylaczono !== null && !Number.isNaN(wylaczono) && (koniec === null || wylaczono < koniec) ? Math.max(wylaczono, start) : koniec,
      kolor: k, kolorTekstu: kolorTekstu(k),
    });
  }
  // V1: rewizja z oknem; aktywna i zaplanowana zawsze, zakończona/wyłączona tylko z datą startu (historia).
  for (const r of rewizje) {
    if (r.effectiveStatus !== 'ACTIVE' && r.effectiveStatus !== 'SCHEDULED' && r.effectiveStatus !== 'ENDED' && r.effectiveStatus !== 'DISABLED') continue;
    if (!r.startsAt && r.effectiveStatus !== 'ACTIVE') continue;
    const start = ms(r.startsAt) || ms(r.activatedAt) || ms(r.updatedAt);
    if (Number.isNaN(start)) continue;
    const koniec = r.endsAt ? ms(r.endsAt) : null;
    const wylaczono = r.effectiveStatus === 'DISABLED' && r.disabledAt ? ms(r.disabledAt) : null;
    const k = kolor(r.themeId);
    bloki.push({
      klucz: `v1-${r.id}`, zastosowanie: null, v1: r, themeId: r.themeId, nazwa: r.name, miniatura: r.thumbnail ?? miniatury.get(r.themeId) ?? null,
      zakres: 'GLOBAL', adresaci: [], priorytet: 0, stan: r.effectiveStatus, start, koniec,
      koniecRysowany: wylaczono !== null && !Number.isNaN(wylaczono) && (koniec === null || wylaczono < koniec) ? Math.max(wylaczono, start) : koniec,
      kolor: k, kolorTekstu: kolorTekstu(k),
    });
  }
  return bloki.sort((a, b) => a.start - b.start || a.klucz.localeCompare(b.klucz));
}

// ── KOLIZJE — dwa bieżące zastosowania na tych samych adresatów w tym samym czasie ────────────────────

export interface Kolizja {
  a: string;
  b: string;
  /** Ten sam priorytet — o zwycięzcy nie decyduje priorytet (najpoważniejsze ostrzeżenie). */
  remis: boolean;
}

const nachodzaCzasem = (a: BlokHarmonogramu, b: BlokHarmonogramu) =>
  a.start < (b.koniec ?? Infinity) && b.start < (a.koniec ?? Infinity);

function wspolniAdresaci(a: BlokHarmonogramu, b: BlokHarmonogramu): boolean {
  if (a.zakres !== b.zakres) return false;
  if (a.zakres === 'GLOBAL') return true;
  const ids = new Set(a.adresaci.map((t) => t.id.toLowerCase()));
  return b.adresaci.some((t) => ids.has(t.id.toLowerCase()));
}

/** Kolizja = ten sam zakres, wspólny adresat, nachodzące terminy; tylko zastosowania aktywne i zaplanowane. */
export function wykryjKolizje(bloki: readonly BlokHarmonogramu[]): Kolizja[] {
  const biezace = bloki.filter(bierzacy);
  const wynik: Kolizja[] = [];
  for (let i = 0; i < biezace.length; i++) {
    for (let j = i + 1; j < biezace.length; j++) {
      const a = biezace[i]!; const b = biezace[j]!;
      if (wspolniAdresaci(a, b) && nachodzaCzasem(a, b)) wynik.push({ a: a.klucz, b: b.klucz, remis: a.priorytet === b.priorytet });
    }
  }
  return wynik;
}

export function kolizjeBloku(klucz: string, kolizje: readonly Kolizja[]): { z: string; remis: boolean }[] {
  return kolizje.filter((k) => k.a === klucz || k.b === klucz).map((k) => ({ z: k.a === klucz ? k.b : k.a, remis: k.remis }));
}

// ── OŚ CZASU — dni warszawskie ─────────────────────────────────────────────────────────────────────────

export interface DzienOsi {
  /** Północ w Warszawie (ms UTC). */
  start: number;
  koniec: number;
  dzien: number;
  miesiac: number;
  rok: number;
  /** 0 = poniedziałek … 6 = niedziela. */
  dzienTygodnia: number;
  weekend: boolean;
  dzis: boolean;
}

function polnocWarszawy(rok: number, miesiac: number, dzien: number): number {
  const d = new Date(Date.UTC(rok, miesiac - 1, dzien));
  const w = warszawaNaUtc({ rok: d.getUTCFullYear(), miesiac: d.getUTCMonth() + 1, dzien: d.getUTCDate(), godzina: 0, minuta: 0 });
  // Północ w Warszawie nigdy nie wypada w zmianie czasu (02:00/03:00) — zawsze jednoznaczna.
  return w.rodzaj === 'JEDNOZNACZNA' ? w.utc.getTime() : Date.UTC(rok, miesiac - 1, dzien);
}

/**
 * Dni osi: `liczbaDni` dni, „dziś” w środku zakresu; `przesuniecie` = ile zakresów w przód (+) albo w tył (−).
 */
export function dniOsi(teraz: Date, liczbaDni: number, przesuniecie = 0): DzienOsi[] {
  const dzis = utcNaWarszawe(teraz);
  const pierwszy = -Math.floor(liczbaDni / 2) + przesuniecie * liczbaDni;
  const dni: DzienOsi[] = [];
  for (let i = 0; i < liczbaDni; i++) {
    const d = new Date(Date.UTC(dzis.rok, dzis.miesiac - 1, dzis.dzien + pierwszy + i));
    const rok = d.getUTCFullYear(); const miesiac = d.getUTCMonth() + 1; const dzien = d.getUTCDate();
    const dzienTygodnia = (d.getUTCDay() + 6) % 7;
    dni.push({
      start: polnocWarszawy(rok, miesiac, dzien), koniec: polnocWarszawy(rok, miesiac, dzien + 1),
      dzien, miesiac, rok, dzienTygodnia, weekend: dzienTygodnia >= 5, dzis: pierwszy + i === 0,
    });
  }
  return dni;
}

/** Położenie chwili na osi: 0 = lewa krawędź pierwszego dnia, 1 = prawa krawędź ostatniego (przycięte). */
export function pozycja(t: number, dni: readonly DzienOsi[]): number {
  const pierwszy = dni[0]; const ostatni = dni[dni.length - 1];
  if (!pierwszy || !ostatni) return 0;
  if (t <= pierwszy.start) return 0;
  if (t >= ostatni.koniec) return 1;
  const i = dni.findIndex((d) => t >= d.start && t < d.koniec);
  const d = dni[i]!;
  return (i + (t - d.start) / (d.koniec - d.start)) / dni.length;
}

/** Czy blok jest choć częściowo w zakresie osi. */
export function wZakresie(b: BlokHarmonogramu, dni: readonly DzienOsi[]): boolean {
  const pierwszy = dni[0]; const ostatni = dni[dni.length - 1];
  if (!pierwszy || !ostatni) return false;
  return b.start < ostatni.koniec && (b.koniecRysowany ?? Infinity) > pierwszy.start;
}

/**
 * Wiersze w torze: bloki na siebie nachodzące idą pod sobą (zachłannie, od najwcześniejszego startu).
 * `zajetosc` — odcinek, który blok zajmuje w wierszu (krótki blok ma etykietę OBOK siebie, więc rezerwuje
 * także jej miejsce, a nie tylko swoje kilka godzin). Domyślnie sam blok.
 */
export function ulozWiersze(
  bloki: readonly BlokHarmonogramu[],
  zajetosc: (b: BlokHarmonogramu) => [number, number] = (b) => [b.start, b.koniecRysowany ?? Infinity],
): Map<string, number> {
  const konceWierszy: number[] = [];
  const wynik = new Map<string, number>();
  const odcinki = bloki.map((b) => ({ b, z: zajetosc(b) }));
  for (const { b, z: [od, doKiedy] } of odcinki.sort((x, y) => x.z[0] - y.z[0] || x.b.klucz.localeCompare(y.b.klucz))) {
    let w = konceWierszy.findIndex((k) => k <= od);
    if (w === -1) { w = konceWierszy.length; konceWierszy.push(0); }
    konceWierszy[w] = doKiedy;
    wynik.set(b.klucz, w);
  }
  return wynik;
}

// ── OPISY ─────────────────────────────────────────────────────────────────────────────────────────────

/** „6 dni”, „1 dzień”, „5 h”, „bez końca”. Koniec 23:59 liczony jak pełny dzień (zaokrąglenie). */
export function czasTrwania(start: number, koniec: number | null): string {
  if (koniec === null) return 'bez końca';
  const godziny = (koniec - start) / 3_600_000;
  if (godziny < 23.5) return `${Math.max(1, Math.round(godziny))} h`;
  const dni = Math.round(godziny / 24);
  return dni === 1 ? '1 dzień' : `${dni} dni`;
}

export interface KpiHarmonogramu {
  aktywne: number;
  zaplanowane: number;
  bezKonca: number;
  kolizje: number;
  remisy: number;
  najblizszeStarty: BlokHarmonogramu[];
  najblizszeKonce: BlokHarmonogramu[];
}

/** Liczby dla całego systemu (niezależne od filtrów widoku) — „co się dzieje” jednym spojrzeniem. */
export function kpi(bloki: readonly BlokHarmonogramu[], kolizje: readonly Kolizja[], teraz: Date, ile = 3): KpiHarmonogramu {
  const t = teraz.getTime();
  const biezace = bloki.filter(bierzacy);
  return {
    aktywne: bloki.filter((b) => b.stan === 'ACTIVE').length,
    zaplanowane: bloki.filter((b) => b.stan === 'SCHEDULED').length,
    bezKonca: biezace.filter((b) => b.koniec === null).length,
    kolizje: kolizje.length,
    remisy: kolizje.filter((k) => k.remis).length,
    najblizszeStarty: bloki.filter((b) => b.stan === 'SCHEDULED' && b.start >= t).sort((a, b) => a.start - b.start).slice(0, ile),
    najblizszeKonce: biezace.filter((b) => b.koniec !== null && b.koniec >= t)
      .sort((a, b) => (a.koniec ?? 0) - (b.koniec ?? 0)).slice(0, ile),
  };
}

/** Krótki opis adresatów na bloku: „Warszawa”, „III LO +2”, „1 osoba”, „Wszyscy”. */
export function adresaciKrotko(b: Pick<BlokHarmonogramu, 'zakres' | 'adresaci'>): string {
  if (b.zakres === 'GLOBAL') return 'Wszyscy';
  if (b.zakres === 'USER') {
    const n = b.adresaci.length;
    const kilka = n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14);
    return n === 1 ? '1 osoba' : `${n} ${kilka ? 'osoby' : 'osób'}`;
  }
  const [pierwszy, ...reszta] = b.adresaci;
  if (!pierwszy) return '—';
  return reszta.length ? `${pierwszy.name} +${reszta.length}` : pierwszy.name;
}
